// skipcq: JS-W1028, JS-0128
import React, { useId } from 'react';
import { Box, useTheme } from '@mui/material';

/**
 * Simple sparkline component that renders an SVG trend line
 * @param {Array} data - Array of numeric values to plot
 * @param {number} width - Width of the sparkline in pixels
 * @param {number} height - Height of the sparkline in pixels
 * @param {string} color - Color of the line
 * @param {number} strokeWidth - Width of the stroke
 */
function Sparkline({
  data = [],
  width = 60,
  height = 20,
  color = 'primary.main',
  strokeWidth = 1.5,
}) {
  const theme = useTheme();
  const grainFilterId = useId().replace(/:/g, '');

  if (!data || data.length === 0) {
    return null;
  }

  // Resolve the color from theme
  const resolvedColor = color.includes('.')
    ? color.split('.').reduce((obj, key) => obj?.[key], theme.palette)
    : color;

  // Handle responsive width
  const isResponsive = typeof width === 'string' && width.includes('%');
  const svgWidth = isResponsive ? '100%' : width;
  const pointsWidth = isResponsive ? 100 : width; // Use 100 as reference for percentage-based calculations

  // Get min and max values for scaling
  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);
  const valueRange = maxValue - minValue;

  const filterRef = `sparkline-grain-${grainFilterId}`;

  const grainDefs = (
    <defs>
      <filter
        id={filterRef}
        x="-15%"
        y="-15%"
        width="130%"
        height="130%"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.9"
          numOctaves="2"
          result="noise"
        />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="0.45" />
      </filter>
    </defs>
  );

  // If all values are the same, show a flat line
  if (valueRange === 0) {
    const y = height / 2;
    return (
      <Box
        sx={{
          display: 'inline-block',
          ml: 1,
          width: isResponsive ? '100%' : 'auto',
        }}
      >
        <svg
          width={svgWidth}
          height={height}
          viewBox={isResponsive ? `0 0 ${pointsWidth} ${height}` : undefined}
        >
          {grainDefs}
          <line
            x1={0}
            y1={y}
            x2={pointsWidth}
            y2={y}
            stroke={resolvedColor}
            strokeWidth={strokeWidth}
            fill="none"
            filter={`url(#${filterRef})`}
            strokeLinecap="round"
          />
        </svg>
      </Box>
    );
  }

  // Calculate points for the path
  const points = data
    .map((value, index) => {
      const xCoord = (index / (data.length - 1)) * pointsWidth;
      const yCoord = height - ((value - minValue) / valueRange) * height;
      return `${xCoord},${yCoord}`;
    })
    .join(' ');

  return (
    <Box
      sx={{
        display: 'inline-block',
        ml: 1,
        width: isResponsive ? '100%' : 'auto',
      }}
    >
      <svg
        width={svgWidth}
        height={height}
        viewBox={isResponsive ? `0 0 ${pointsWidth} ${height}` : undefined}
      >
        {grainDefs}
        <polyline
          points={points}
          fill="none"
          stroke={resolvedColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${filterRef})`}
        />
      </svg>
    </Box>
  );
}

export default Sparkline;
